import * as React from 'react';
import { motion } from 'framer-motion';
import { FileText, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

interface AuditEntry {
  id: string;
  user_id: string;
  user_name: string;
  operation_type: string;
  previous_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  description: string | null;
  performed_at: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const OPERATION_LABELS: Record<string, string> = {
  cancelamento_venda: 'Cancelamento de Venda',
  estorno_venda: 'Estorno de Venda',
  alteracao_preco: 'Alteração de Preço',
  exclusao_produto: 'Exclusão de Produto',
  desconto_aplicado: 'Desconto Aplicado',
  sangria: 'Sangria',
  alteracao_usuario: 'Alteração de Usuário',
  desativacao_usuario: 'Desativação de Usuário',
  criacao_usuario: 'Criação de Usuário',
};

const OPERATION_VARIANTS: Record<string, 'default' | 'destructive' | 'warning' | 'secondary'> = {
  cancelamento_venda: 'destructive',
  estorno_venda: 'destructive',
  alteracao_preco: 'warning',
  exclusao_produto: 'destructive',
  desconto_aplicado: 'warning',
  sangria: 'secondary',
  alteracao_usuario: 'default',
  desativacao_usuario: 'warning',
  criacao_usuario: 'default',
};

function AuditPageContent() {
  const [entries, setEntries] = React.useState<AuditEntry[]>([]);
  const [pagination, setPagination] = React.useState<Pagination>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [operations, setOperations] = React.useState<string[]>([]);

  // Filters
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [operationType, setOperationType] = React.useState('');
  const [page, setPage] = React.useState(1);

  const fetchOperations = React.useCallback(async () => {
    try {
      const response = await apiFetch('/api/audit/operations');
      if (response.ok) {
        const data = await response.json();
        setOperations(data.operations);
      }
    } catch {
      // Ignore
    }
  }, []);

  const fetchAudit = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (operationType) params.set('operationType', operationType);

      const response = await apiFetch(`/api/audit?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Erro ao carregar log de auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, startDate, endDate, operationType]);

  React.useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  React.useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  function handleFilter() {
    setPage(1);
    fetchAudit();
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const columns: Column<AuditEntry>[] = [
    {
      key: 'performed_at',
      header: 'Data/Hora',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDate(row.performed_at)}
        </span>
      ),
    },
    {
      key: 'operation_type',
      header: 'Operação',
      render: (row) => (
        <Badge variant={OPERATION_VARIANTS[row.operation_type] || 'default'}>
          {OPERATION_LABELS[row.operation_type] || row.operation_type}
        </Badge>
      ),
    },
    {
      key: 'user_name',
      header: 'Usuário',
      render: (row) => <span className="font-medium">{row.user_name}</span>,
    },
    {
      key: 'description',
      header: 'Descrição',
      render: (row) => (
        <span className="text-sm text-muted-foreground max-w-xs truncate block">
          {row.description || '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          Log de Auditoria
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Registro imutável de operações críticas do sistema
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Data Início
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="flex h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Data Fim
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="flex h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Tipo de Operação
          </label>
          <select
            value={operationType}
            onChange={(e) => setOperationType(e.target.value)}
            className="flex h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          >
            <option value="">Todas</option>
            {operations.map((op) => (
              <option key={op} value={op}>
                {OPERATION_LABELS[op] || op}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" variant="outline" onClick={handleFilter}>
          <Search className="h-3.5 w-3.5" />
          Filtrar
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={entries}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum registro de auditoria encontrado"
          />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Mostrando {entries.length} de {pagination.total} registros
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AuditPage() {
  return (
    <RequireRole roles={['administrador']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AuditPageContent />
      </motion.div>
    </RequireRole>
  );
}
